import React, { createContext, useReducer } from "react";
import AppReducer from "./AppReducer";

import * as api from "../app/api";

const searchParameters = new URLSearchParams(window.location.search);
const promotionCode = searchParameters.get("pc") || "";
const arePromotionsAllowed = searchParameters.get("p") === "1" || !!promotionCode;

const initialState = {
  isProcessing: false,
  error: {
    isFatal: false,
    message: ""
  },
  configuration: {
    id: "",
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    promotion: promotionCode,
    arePromotionsAllowed,
    discount: 0,
    sku: "starter",
    seats: 10,
    license: "user",
    pricePerUser: 0,
    pricePerMonth: 0,
    pricePerYear: 0,
    vpnPricePerMonth: 0,
    vpnPricePerYear: 0,
    vpnSeats: 0
  },
  pricing: {
    user: {
      professional: 0,
      enterprise: 0,
      starter: 0
    },
    session: {
      professional: 0,
      starter: 0,
      enterprise: 0
    },
    discount: 0,
    vpn: 0
  },
  payment: {
    subscriptionId: "",
    secret: "",
  },
  activation: {
    activationKey: "",
    installationId: "",
    customer: "",
    customerType: "",
    startDate: null,
    endDate: null,
    sku: "",
  },
  license: {
    key: ""
  }
};

export const AppContext = createContext(initialState);

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AppReducer, initialState);

  const setIsProcessing = (enable) => dispatch({
    type: "SET_PROCESSING_SPINNER",
    payload: enable
  });

  const setError = (message) => dispatch({
    type: "SET_GLOBAL_ERROR",
    payload: {
      fatal: true,
      message
    }
  });

  const setFatalError = (message) => dispatch({
    type: "SET_GLOBAL_ERROR",
    payload: {
      fatal: false,
      message
    }
  });

  const updateConfiguration = (key) => async (value) => {
    dispatch({
      type: "UPDATE_CONFIGURATION",
      payload: {
        [key]: value
      }
    });

    if (key === "license" || key === "sku" || key === "seats" || key === "vpnSeats" || key === "promotion") {
      dispatch({ type: "UPDATE_PRICES" });

      dispatch({
        type: "SET_SUBSCRIPTION",
        payload: {
          secret: "",
          subscriptionId: ""
        }
      });
    }

    if (key === "email") {
      dispatch({
        type: "SET_SUBSCRIPTION",
        payload: {
          secret: "",
          subscriptionId: ""
        }
      });
    }
    if (key === "seats" && state.configuration.vpnSeats !== 0) {
      if (state.configuration.vpnSeats < value) {
        updateConfiguration("vpnSeats")(value)
      }
    }
  }

  const setInstallationId = (id) => dispatch({
    type: "SET_INSTALLATION_ID",
    payload: id
  });

  const setActivationKey = (key) => {
    if (!key) {
      dispatch({ type: "CLEAR_ACTIVATION_KEY" });
      return;
    }

    dispatch({
      type: "SET_ACTIVATION_KEY",
      payload: (key || "")
        .replace("-----BEGIN ACTIVATION KEY-----", "")
        .replace("-----END ACTIVATION KEY-----", "")
        .replace(/\n/g, "")
    });
  }

  const validateEmail = async (email) => {
    const data = await api.validateEmail(state.id, email);

    if (data.id) {
      updateConfiguration("id")(data.id);
    }

    if (data.verified) {
      updateConfiguration("email")(email);
    }

    if (data.first_name) {
      updateConfiguration("firstName")(data.first_name);
    }

    if (data.last_name) {
      updateConfiguration("lastName")(data.last_name);
    }

    if (data.company) {
      updateConfiguration("company")(data.company);
    }
  }

  const validateOTP = async (code) => {
    const { email } = await api.validateOTP(state.configuration.id, code);
    updateConfiguration("email")(email);
  }

  const createSubscription = async () => {
    const { configuration } = state;

    const { secret, subscriptionId } = await api.createSubscription(
      configuration.id,
      configuration.firstName,
      configuration.lastName,
      configuration.company,
      configuration.seats,
      configuration.sku,
      configuration.license,
      configuration.promotion,
      configuration.vpnSeats
    );

    dispatch({
      type: "SET_SUBSCRIPTION",
      payload: { secret, subscriptionId }
    });
  }

  const updateSubscription = async () => {
    const { configuration, payment } = state;

    await api.updateSubscription(
      configuration.id,
      payment.subscriptionId,
      configuration.firstName,
      configuration.lastName,
      configuration.company,
    );
  }

  const fetchPrices = async () => {
    const promotion = state.configuration.promotion;

    const prices = await api.getPrices(state.configuration.promotion);

    if (promotion && prices.discount === 0) {
      setError(`Promotion code not found!`);
    }

    dispatch({ type: "SET_PRICING", payload: prices });
    dispatch({ type: "UPDATE_PRICES" });
  }

  const resetPromotion = async () => {
    updateConfiguration("promotion")("");
    const prices = await api.getPrices();
    dispatch({ type: "SET_PRICING", payload: prices });
    dispatch({ type: "UPDATE_PRICES" });
  }

  const fetchLicenseActivationKey = async (customerId, paymentIntentId) => {
    setIsProcessing(true);

    const checkInterval = setInterval(async () => {
      const key = await api.getLicenseActivationKey(customerId, paymentIntentId);

      if (key) {
        setIsProcessing(false);

        dispatch({
          type: "SET_ACTIVATION_KEY",
          payload: key
        });

        clearInterval(checkInterval);
      }
    }, 1000);
  }

  const activateLicense = async () => {
    setIsProcessing(true);

    try {
      const { activationKey, installationId } = state.activation;

      if (!activationKey || !installationId) {
        throw new Error(`Cannot activate license. Missing argument(s).`);
      }

      const key = await api.activateLicense(installationId, activationKey);

      dispatch({
        type: "SET_LICENSE_KEY",
        payload: key
      });
    } catch (e) {
      console.error(e);
      setError(e.message);
    }

    setIsProcessing(false);
  }

  return (
    <AppContext.Provider value={{
      isProcessing: state.isProcessing,
      configuration: state.configuration,
      payment: state.payment,
      pricing: state.pricing,
      activation: state.activation,
      license: state.license,
      error: state.error,
      setError,
      setFatalError,
      setIsProcessing,
      setFirstName: updateConfiguration("firstName"),
      setLastName: updateConfiguration("lastName"),
      setCompany: updateConfiguration("company"),
      setSeats: updateConfiguration("seats"),
      setVpnSeats: updateConfiguration("vpnSeats"),
      setSKU: updateConfiguration("sku"),
      setLicense: updateConfiguration("license"),
      setPromotion: updateConfiguration("promotion"),
      setEmail: updateConfiguration("email"),
      setActivationKey,
      setInstallationId,
      validateEmail,
      validateOTP,
      createSubscription,
      updateSubscription,
      resetPromotion,
      fetchPrices,
      fetchLicenseActivationKey,
      activateLicense
    }}>
      {children}
    </AppContext.Provider>
  )
}